import { useState } from 'react'
import { useFormatMessage } from '@/translations/useFormatMessage'
import { CircularProgress } from '@mui/material'
import PhoneInput, { getCountryCallingCode, isValidPhoneNumber } from 'react-phone-number-input'
import type { Country } from 'react-phone-number-input'
import 'react-phone-number-input/style.css'
import { requestOtp } from '@/shared/api/otp'
import { normalizeError } from '@/lib/apiErrors'
import { getErrorMessageKey } from '../../lib/errorCodeMapper'
import {
	StyledContentBox,
	StyledPhoneInputContainer,
	StyledErrorBox,
	StyledErrorText,
	StyledButton,
	StyledFooterBox,
	StyledButtonContainer,
	StyledEmailHelperText,
	StyledEmailFieldBox,
	StyledEmailLabel,
	StyledEmailInput,
} from '../../SignInModal.styled'
import { StyledFormInputsWrapper, StyledLoadingContent } from './SignInForm.styled'
import type { StepComponentProps } from '../../SignInModal.types'

export function SignInForm({ state, setState, locationId }: StepComponentProps) {
	const { t } = useFormatMessage()
	const [emailInput, setEmailInput] = useState('')
	const [selectedCountry, setSelectedCountry] = useState('US')
	const [emailHasError, setEmailHasError] = useState(false)

	const handlePhoneChange = (value: string | undefined) => {
		setState((prev) => ({
			...prev,
			phoneOrEmailInput: value || '',
			errorMessage: null,
		}))
	}

	const handleCountryChange = (country: string | undefined) => {
		if (country) {
			setSelectedCountry(country)
			// Reset email if switching to US
			if (country === 'US' && emailInput) {
				setEmailInput('')
			}
		}
	}

	const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setEmailInput(e.target.value)
		setEmailHasError(false)
		setState((prev) => ({
			...prev,
			errorMessage: null,
		}))
	}

	const isNonUSCountry = selectedCountry !== 'US'

	const validateEmail = (email: string): boolean => {
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
		return emailRegex.test(email)
	}

	const handleSendCode = async () => {
		if (!state.phoneOrEmailInput.trim()) {
			const errorMessage = t('signin:errors:emptyPhone')
			setState((prev) => ({
				...prev,
				errorMessage,
			}))
			return
		}

		if (isNonUSCountry && !emailInput.trim()) {
			const errorMessage = t('signin:errors:emptyEmail')
			setEmailHasError(true)
			setState((prev) => ({
				...prev,
				errorMessage,
			}))
			return
		}

		if (isNonUSCountry && !validateEmail(emailInput)) {
			const errorMessage = t('signin:errors:invalidEmailFormat')
			setEmailHasError(true)
			setState((prev) => ({
				...prev,
				errorMessage,
			}))
			return
		}

		setState((prev) => ({
			...prev,
			isRequestInProgress: true,
			errorMessage: null,
		}))

		try {
			const channel = selectedCountry === 'US' ? 'SMS' : 'EMAIL'
			const identifier = channel === 'SMS' ? state.phoneOrEmailInput : emailInput

			const response = await requestOtp({
				channel,
				identifier,
				locationId,
			})

			setState((prev) => ({
				...prev,
				isVerifyStepShown: true,
				isRequestInProgress: false,
				correlationId: response.correlationId,
				channel,
				identifier,
			}))
		} catch (err) {
			const apiError = normalizeError(err)
			const errorKey = getErrorMessageKey({
				status: apiError.status ?? 0,
				message: apiError.message,
			})
			const errorMessage = t(errorKey)

			setState((prev) => ({
				...prev,
				isRequestInProgress: false,
				errorMessage,
			}))
		}
	}

	const isPhoneValid = isValidPhoneNumber(state.phoneOrEmailInput)
	const isEmailValid = !isNonUSCountry || validateEmail(emailInput)
	const isFormValid = isPhoneValid && isEmailValid

	const callingCode = `+${getCountryCallingCode(selectedCountry as Country)}`

	return (
		<>
			<StyledContentBox>
				<StyledFormInputsWrapper>
					<StyledPhoneInputContainer
						style={{ ['--calling-code' as string]: `'${callingCode}'` }}
					>
						<PhoneInput
							defaultCountry="US"
							value={state.phoneOrEmailInput}
							onChange={handlePhoneChange}
							onCountryChange={handleCountryChange}
							disabled={state.isRequestInProgress}
							placeholder={t('signin:signInForm:phoneLabel')}
						/>
					</StyledPhoneInputContainer>

					{isNonUSCountry && (
						<>
							<StyledEmailHelperText>
								{t('signin:signInForm:emailHelper')}
							</StyledEmailHelperText>

							<StyledEmailFieldBox hasError={emailHasError}>
								<StyledEmailLabel htmlFor="signin-email">
									{t('signin:signInForm:emailLabel')}
								</StyledEmailLabel>
								<StyledEmailInput
									id="signin-email"
									type="email"
									value={emailInput}
									onChange={handleEmailChange}
									disabled={state.isRequestInProgress}
									aria-label={t('signin:signInForm:emailAriaLabel')}
									aria-required="true"
								/>
							</StyledEmailFieldBox>
						</>
					)}

					{state.errorMessage && (
						<StyledErrorBox role="alert">
							<StyledErrorText>{state.errorMessage}</StyledErrorText>
						</StyledErrorBox>
					)}
				</StyledFormInputsWrapper>
			</StyledContentBox>

			<StyledFooterBox>
				<StyledButtonContainer>
					<StyledButton
						type="button"
						onClick={() => void handleSendCode()}
						disabled={!isFormValid || state.isRequestInProgress}
					>
						{state.isRequestInProgress ? (
							<StyledLoadingContent>
								<CircularProgress size={16} sx={{ color: 'inherit' }} />
								{t('signin:signInForm:sendingButton')}
							</StyledLoadingContent>
						) : (
							t('signin:signInForm:sendButton')
						)}
					</StyledButton>
				</StyledButtonContainer>
			</StyledFooterBox>
		</>
	)
}
